<template>
  <div>
    <section>
      <div class="section-container">
        <vs-select id="blueEnergy" v-model="selectedEnergyType">
          <vs-select-item
            v-for="(type, index) in energyTypes"
            :key="index"
            :value="type"
            :text="type.name"
          />
        </vs-select>
        <div class="button">
          <vs-button
            type="filled"
            size="small"
            icon="dns"
            color="#19535F"
            @click="getEnergySuitabilities()"
            >Calculate Energy Suitabilities</vs-button
          >
        </div>
      </div>
    </section>
    <section v-if="showResults" id="results">
      <vs-divider>Suitability Results</vs-divider>
      <div class="grid-results">
        <div class="header-item">Resource</div>
        <div class="header-item">Structural</div>
        <div class="header-item">Operational</div>
        <div class="header-item">Consumer Centers</div>
        <div class="header-item">Opportunities</div>
        <div class="result-item">
          <vs-chip>
            <vs-avatar
              :icon="$_utilsMixin_getCategoryIcon(results.resource)"
              text
              class="index-avatar"
            />
            <span
              v-if="parsedResults.resource !== noDataValue"
              class="label-result"
              >{{ parsedResults.resource }}%</span
            >
            <span v-else class="label-result">No Data</span>
          </vs-chip>
          <span
            :style="{
              backgroundColor: $_utilsMixin_getCategoryColor(results.resource)
            }"
            class="dot"
          />
        </div>
        <div class="result-item">
          <vs-chip>
            <vs-avatar
              :icon="$_utilsMixin_getCategoryIcon(results.structural)"
              text
              class="index-avatar"
            />
            <span
              v-if="parsedResults.structural !== noDataValue"
              class="label-result"
              >{{ parsedResults.structural }}%</span
            >
            <span v-else class="label-result">No Data</span>
          </vs-chip>
          <span
            :style="{
              backgroundColor: $_utilsMixin_getCategoryColor(results.structural)
            }"
            class="dot"
          />
        </div>
        <div class="result-item">
          <vs-chip>
            <vs-avatar
              :icon="$_utilsMixin_getCategoryIcon(results.operational)"
              text
              class="index-avatar"
            />
            <span
              v-if="parsedResults.operational !== noDataValue"
              class="label-result"
              >{{ parsedResults.operational }}%</span
            >
            <span v-else class="label-result">No Data</span>
          </vs-chip>
          <span
            :style="{
              backgroundColor: $_utilsMixin_getCategoryColor(
                results.operational
              )
            }"
            class="dot"
          />
        </div>
        <div class="result-item">
          <vs-chip>
            <vs-avatar
              :icon="$_utilsMixin_getCategoryIcon(results.centers)"
              text
              class="index-avatar"
            />
            <span
              v-if="parsedResults.centers !== noDataValue"
              class="label-result"
              >{{ parsedResults.centers }}%</span
            >
            <span v-else class="label-result">No Data</span>
          </vs-chip>
          <span
            :style="{
              backgroundColor: $_utilsMixin_getCategoryColor(results.centers)
            }"
            class="dot"
          />
        </div>
        <div class="result-item">
          <vs-chip>
            <vs-avatar
              :icon="$_utilsMixin_getCategoryIcon(results.opportunities)"
              text
              class="index-avatar"
            />
            <span
              v-if="parsedResults.opportunities !== noDataValue"
              class="label-result"
              >{{ parsedResults.opportunities }}%</span
            >
            <span v-else class="label-result">No Data</span>
          </vs-chip>
          <span
            :style="{
              backgroundColor: $_utilsMixin_getCategoryColor(
                results.opportunities
              )
            }"
            class="dot"
          />
        </div>
      </div>
      <label class="footer-label">
        * Locations with depths higher than 500m are not considered suitable for
        Marine Renewable Energies, the result is expresed as "No Data"
      </label>
    </section>
  </div>
</template>
<script>
import globalData from "@/data/global.js";
import { SECTORS, NO_DATA_VALUE } from "@/constants.js";
import { getSuitability } from "@/api/thredds.js";
import { mapState } from "vuex";

export default {
  data() {
    return {
      energySector: {},
      noDataValue: NO_DATA_VALUE,
      globalEnergyPath: "",
      selectedEnergyType: {},
      energyTypes: [],
      showResults: false,
      results: {
        resource: 0,
        structural: 0,
        operational: 0,
        centers: 0,
        opportunities: 0
      }
    };
  },
  computed: {
    ...mapState("map", {
      marker: state => state.marker
    }),
    hasResults() {
      return this.results.length > 0;
    },
    parsedResults() {
      return {
        resource:
          this.results.resource >= 0
            ? parseInt(this.results.resource * 100)
            : this.noDataValue,
        structural:
          this.results.structural >= 0
            ? parseInt(this.results.structural * 100)
            : this.noDataValue,
        operational:
          this.results.operational >= 0
            ? parseInt(this.results.operational * 100)
            : this.noDataValue,
        centers:
          this.results.centers >= 0
            ? parseInt(this.results.centers * 100)
            : this.noDataValue,
        opportunities:
          this.results.opportunities >= 0
            ? parseInt(this.results.opportunities * 100)
            : this.noDataValue
      };
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.getEnergySector();
      this.loadEnergyTypes();
      this.selectedEnergyType = this.energyTypes[0];
      this.globalEnergyPath = this.extractGlobalEnergyPath();
    },
    getEnergySector() {
      const energySector = globalData.sectors.find(sector => {
        return sector.alias === SECTORS.energy.name;
      });
      this.energySector = energySector;
    },
    loadEnergyTypes() {
      this.energyTypes.push({
        name: "Wave",
        variables: {
          resource: this.energySector.types[0].layers[1].name,
          structural: this.energySector.types[1].layers[1].name,
          operational: this.energySector.types[2].layers[0].name,
          centers: this.energySector.types[3].layers[0].name,
          opportunities: this.energySector.types[4].layers[1].name
        }
      });
      this.energyTypes.push({
        name: "Wind",
        variables: {
          resource: this.energySector.types[0].layers[0].name,
          structural: this.energySector.types[1].layers[0].name,
          operational: this.energySector.types[2].layers[0].name,
          centers: this.energySector.types[3].layers[0].name,
          opportunities: this.energySector.types[4].layers[0].name
        }
      });
    },
    getEnergySuitabilities() {
      this.requestEnergySuitabilities();
      this.showResults = true;
    },
    async requestEnergySuitabilities() {
      this.results.resource = await this.getSuitabilityValue(
        "EnergyResourceSuitability/EnergyResourceSuitability.nc",
        this.selectedEnergyType.variables.resource
      );
      this.results.structural = await this.getSuitabilityValue(
        "StructuralSuitability/StructuralSuitability.nc",
        this.selectedEnergyType.variables.structural
      );
      this.results.operational = await this.getSuitabilityValue(
        "OperationalSuitability/OperationalSuitability.nc",
        this.selectedEnergyType.variables.operational
      );
      this.results.centers = await this.getSuitabilityValue(
        "ConsumerCenters/ConsumerCenters.nc",
        this.selectedEnergyType.variables.centers
      );
      this.results.opportunities = await this.getSuitabilityValue(
        "RenewableEnergyOpportunities/RenewableEnergyOpportunities.nc",
        this.selectedEnergyType.variables.opportunities
      );
    },
    async getSuitabilityValue(path, varName) {
      const fullPath = `${this.globalEnergyPath}/${path}`;
      const params = {
        point: this.marker,
        path: fullPath,
        variableName: varName
      };
      const value = await getSuitability(params);
      return parseFloat(value);
    },
    extractGlobalEnergyPath() {
      return `/${globalData.name}/${this.energySector.name}`;
    }
  }
};
</script>
<style scoped>
.grid-results {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  /* grid-template-rows: 9rem 1fr 0.4fr 1rem; */
  grid-template-rows: 20px 1fr;
  grid-gap: 1rem;
  margin-bottom: 2rem;
  margin-top: 2rem;
}

.header-item {
  font-size: 1rem;
  font-weight: bold;
}

.result-item {
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  justify-content: center;
}

.button {
  display: flex;
  justify-content: flex-end;
}

.label-result {
  font-size: 1.4rem;
  margin-right: 5px;
}
</style>
